import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { Report, ReportDefinition, ReportIndicator, ReportingOverview, ReportPeriod, ReportSection } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDay, formatMoney } from '../finance/money';
import { useMe } from '../session/SessionProvider';

const PERIODS: Record<ReportPeriod, string> = {
  last_4_weeks: 'Últimas 4 semanas',
  last_3_months: 'Últimos 3 meses',
  this_year: 'Este año',
  custom: 'Otras fechas',
};

interface Query {
  indicators: ReportIndicator[];
  period: ReportPeriod;
  from: string;
  to: string;
}

/** Ruta del reporte en la API; la misma sirve para verlo (json) y descargarlo (csv). */
export function reportPath(churchId: string, q: Query, format: 'json' | 'csv' = 'json'): string {
  const params = new URLSearchParams({ indicators: q.indicators.join(','), period: q.period });
  if (q.period === 'custom') {
    params.set('from', q.from);
    params.set('to', q.to);
  }
  if (format === 'csv') params.set('format', 'csv');
  return `/reporting/churches/${churchId}/report?${params.toString()}`;
}

const fromDefinition = (d: ReportDefinition): Query => ({ indicators: d.indicators, period: d.period, from: d.from ?? '', to: d.to ?? '' });

/**
 * Motor 24: el reporte se calcula en el momento desde cada motor (regla 19). Aquí solo se elige
 * qué ver; lo que la persona no puede ver en su motor tampoco lo ve aquí.
 */
export function ReportsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const overview = useAsync(
    (signal) => (churchId ? api.get<ReportingOverview>(`/reporting/churches/${churchId}`, signal) : Promise.reject(new Error('Sin contexto'))),
    [churchId],
  );
  const last = useRef<ReportingOverview | null>(null);
  if (overview.status === 'ready') last.current = overview.data;
  const view = overview.status === 'ready' ? overview.data : last.current;

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  return (
    <div className="page">
      <header className="page__header">
        <h1>Reportes</h1>
        <p className="muted">Cifras de la iglesia calculadas al momento desde cada área. Solo ves lo que tu rol permite.</p>
      </header>
      {overview.status === 'loading' && !view ? <Loading /> : null}
      {overview.status === 'error' ? <ErrorState error={overview.error} onRetry={overview.reload} /> : null}
      {view ? <Reports churchId={churchId} overview={view} onChange={overview.reload} /> : null}
    </div>
  );
}

function Reports({ churchId, overview, onChange }: { churchId: string; overview: ReportingOverview; onChange: () => void }) {
  const available = overview.indicators.filter((i) => i.available).map((i) => i.key);
  const [query, setQuery] = useState<Query>({ indicators: available, period: 'last_4_weeks', from: '', to: '' });
  const [title, setTitle] = useState<string | null>(null);
  // Primero los números: los filtros quedan plegados bajo «Cambiar qué veo».
  const [choosing, setChoosing] = useState(false);
  const [report, setReport] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; data?: Report; error?: unknown }>({ status: 'idle' });

  async function run(q: Query, name: string | null) {
    setTitle(name);
    setReport((r) => ({ status: 'loading', data: r.data }));
    try {
      setReport({ status: 'ready', data: await api.get<Report>(reportPath(churchId, q)) });
    } catch (error) {
      setReport({ status: 'error', error });
    }
  }

  // Al entrar se muestra el reporte de las últimas 4 semanas con todo lo que la persona puede ver.
  useEffect(() => {
    if (available.length > 0) void run(query, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId]);

  if (available.length === 0) return <EmptyState title="Tu rol no incluye ningún indicador.">Pide a la administración de la iglesia el acceso que necesitas.</EmptyState>;

  return (
    <div className="reports">
      <div className="reports__main" aria-live="polite">
        <div className="actions">
          <Button variant="secondary" aria-expanded={choosing} onClick={() => setChoosing((c) => !c)}>
            {choosing ? 'Ocultar opciones' : 'Cambiar qué veo'}
          </Button>
        </div>
        {choosing ? (
          <div className="reports__side">
            <Filters query={query} overview={overview} onChange={setQuery} onRun={() => run(query, null)} busy={report.status === 'loading'} csvHref={query.indicators.length ? `/api${reportPath(churchId, query, 'csv')}` : null} />
            <Saved
              churchId={churchId}
              overview={overview}
              query={query}
              onOpen={(d) => {
                const q = fromDefinition(d);
                setQuery(q);
                setChoosing(false);
                void run(q, d.name);
              }}
              onChange={onChange}
            />
          </div>
        ) : null}
        {report.status === 'error' ? <ErrorState error={report.error} /> : null}
        {report.status === 'loading' && !report.data ? <Loading label="Calculando…" /> : null}
        {report.data ? <ReportView report={report.data} title={title} /> : null}
      </div>
    </div>
  );
}

function Filters({ query, overview, onChange, onRun, busy, csvHref }: { query: Query; overview: ReportingOverview; onChange: (q: Query) => void; onRun: () => void; busy: boolean; csvHref: string | null }) {
  function toggle(key: ReportIndicator, on: boolean) {
    const set = new Set(query.indicators);
    if (on) set.add(key);
    else set.delete(key);
    onChange({ ...query, indicators: overview.indicators.map((i) => i.key).filter((k) => set.has(k)) });
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    onRun();
  }
  const incomplete = query.indicators.length === 0 || (query.period === 'custom' && (!query.from || !query.to));
  return (
    <Card title="Qué quieres ver">
      <form className="form" onSubmit={submit}>
        <fieldset className="choices">
          <legend>Indicadores</legend>
          {overview.indicators.map((i) => (
            <label key={i.key} className="choice">
              <input type="checkbox" checked={query.indicators.includes(i.key)} disabled={!i.available} onChange={(e) => toggle(i.key, e.target.checked)} />
              <span>
                {i.label}
                {i.available ? null : <span className="muted small"> · sin acceso</span>}
              </span>
            </label>
          ))}
        </fieldset>
        <div className="field">
          <label htmlFor="report-period">Periodo</label>
          <select id="report-period" value={query.period} onChange={(e) => onChange({ ...query, period: e.target.value as ReportPeriod })}>
            {Object.entries(PERIODS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {query.period === 'custom' ? (
          <div className="form__row">
            <div className="field">
              <label htmlFor="report-from">Desde</label>
              <input id="report-from" type="date" value={query.from} onChange={(e) => onChange({ ...query, from: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="report-to">Hasta</label>
              <input id="report-to" type="date" value={query.to} onChange={(e) => onChange({ ...query, to: e.target.value })} />
            </div>
          </div>
        ) : null}
        <div className="actions actions--wrap">
          <Button type="submit" busy={busy} disabled={incomplete}>
            Ver reporte
          </Button>
          {csvHref && !incomplete ? (
            <a className="btn btn--secondary" href={csvHref} download>
              Descargar CSV
            </a>
          ) : null}
        </div>
      </form>
    </Card>
  );
}

function Saved({ churchId, overview, query, onOpen, onChange }: { churchId: string; overview: ReportingOverview; query: Query; onOpen: (d: ReportDefinition) => void; onChange: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const base = `/reporting/churches/${churchId}/definitions`;

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy('save');
    setMessage(null);
    try {
      await api.post(base, { name, indicators: query.indicators, period: query.period, from: query.period === 'custom' ? query.from : null, to: query.period === 'custom' ? query.to : null });
      setName('');
      setMessage({ tone: 'success', text: 'Reporte guardado.' });
      onChange();
    } catch (e) {
      setMessage({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }

  async function archive(d: ReportDefinition) {
    setBusy(d.id);
    setMessage(null);
    try {
      await api.delete(`${base}/${d.id}`);
      onChange();
    } catch (e) {
      setMessage({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }

  if (!overview.canManage && overview.definitions.length === 0) return null;
  return (
    <Card title="Reportes guardados">
      {overview.definitions.length === 0 ? (
        <p className="muted small">Guarda una selección para abrirla con un toque. Solo se guarda qué ver, nunca las cifras.</p>
      ) : (
        <ul className="list">
          {overview.definitions.map((d) => (
            <li key={d.id} className="list__item">
              <span>
                <strong>{d.name}</strong>
                <span className="muted small"> · {d.period === 'custom' && d.from && d.to ? `${formatDay(d.from)} a ${formatDay(d.to)}` : PERIODS[d.period]}</span>
              </span>
              <span className="actions">
                <Button type="button" variant="secondary" onClick={() => onOpen(d)}>
                  Abrir
                </Button>
                {overview.canManage ? (
                  <Button type="button" variant="ghost" busy={busy === d.id} onClick={() => archive(d)} aria-label={`Archivar ${d.name}`}>
                    Archivar
                  </Button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {overview.canManage ? (
        <form className="form" onSubmit={save}>
          <div className="field">
            <label htmlFor="report-name">Guardar la selección actual como</label>
            <input id="report-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="Ej. Resumen del mes" />
          </div>
          {message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
          <Button type="submit" variant="secondary" busy={busy === 'save'} disabled={!name.trim() || query.indicators.length === 0}>
            Guardar reporte
          </Button>
        </form>
      ) : message ? (
        <Alert tone={message.tone}>{message.text}</Alert>
      ) : null}
    </Card>
  );
}

function ReportView({ report, title }: { report: Report; title: string | null }) {
  return (
    <>
      <p className="reports__period">
        {title ? <strong>{title} · </strong> : null}
        {formatDay(report.period.from)} a {formatDay(report.period.to)}
      </p>
      {report.sections.map((s) => (
        <Section key={s.indicator} section={s} timezone={report.timezone} />
      ))}
    </>
  );
}

function Stats({ items }: { items: [string, number | string][] }) {
  return (
    <div className="stat-row">
      {items.map(([label, value]) => (
        <div key={label} className="stat">
          <span className="stat__value">{value}</span>
          <span className="stat__label">{label}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ section, timezone }: { section: ReportSection; timezone: string }) {
  if (section.status === 'forbidden') {
    return (
      <Card title={section.label}>
        <p className="muted small">Tu rol no permite ver este indicador.</p>
      </Card>
    );
  }
  switch (section.indicator) {
    case 'attendance_by_service': {
      const max = Math.max(1, ...section.rows.map((r) => r.headcount ?? 0));
      return (
        <Card title={section.label}>
          <Stats items={[['Cultos', section.summary.services], ['Con conteo', section.summary.counted], ['Promedio', section.summary.averageHeadcount ?? '—']]} />
          {section.rows.length === 0 ? (
            <p className="muted small">No hubo cultos en este periodo.</p>
          ) : (
            <ul className="bars">
              {section.rows.map((r) => (
                <li key={r.serviceId} className="bars__row">
                  <span className="bars__label">
                    {r.title}
                    <span className="muted small"> · {new Date(r.startsAt).toLocaleDateString('es', { day: 'numeric', month: 'short', timeZone: timezone })}</span>
                  </span>
                  <span className="bars__track" aria-hidden="true">
                    <span className="bars__fill" style={{ width: `${((r.headcount ?? 0) / max) * 100}%` }} />
                  </span>
                  <span className="bars__value">{r.headcount === null ? 'Sin conteo' : `${r.headcount}${r.visitors ? ` (${r.visitors} visitas)` : ''}`}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      );
    }
    case 'membership':
      return (
        <Card title={section.label}>
          <Stats items={[['Vigentes hoy', section.data.active], ['Nuevas', section.data.joined], ['Terminadas', section.data.ended]]} />
        </Card>
      );
    case 'finance_by_month':
      return (
        <Card title={section.label}>
          {section.rows.length === 0 ? (
            <p className="muted small">Sin movimientos en este periodo.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th className="num">Ingresos</th>
                    <th className="num">Gastos</th>
                    <th className="num">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((r) => (
                    <tr key={`${r.month}-${r.currency}`}>
                      <td>{new Date(`${r.month}-15T12:00:00Z`).toLocaleDateString('es', { month: 'short', year: 'numeric', timeZone: 'UTC' })}</td>
                      <td className="num">{formatMoney(r.incomeCents, r.currency)}</td>
                      <td className="num">{formatMoney(r.expenseCents, r.currency)}</td>
                      <td className={`num${r.incomeCents - r.expenseCents < 0 ? ' money--negative' : ''}`}>{formatMoney(r.incomeCents - r.expenseCents, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      );
    case 'service_participation':
      return (
        <Card title={section.label}>
          <Stats items={[['Aceptadas', section.data.accepted], ['Declinadas', section.data.declined], ['Sin respuesta', section.data.pending]]} />
          <p className="muted small">En {section.data.services === 1 ? '1 culto' : `${section.data.services} cultos`} del periodo.</p>
        </Card>
      );
  }
}
